import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "src/shared/hooks/useForm";
import { selectMainState, useDispatch, useSelector } from "src/stateManagement/redux";
import { AddRequirementsInLocation } from "src/stateManagement/redux/slices/location/asyncReducers";
import { normalizeText } from "src/shared/util/normalizeText";
import Swal from "sweetalert2";
import { StatusConditions, TypesOfActivities } from "src/stateManagement/redux/slices/shared/interfaces";
import { IUpdateDiagnosticForActivity } from "src/shared/util/updateDataServer";
import { getTranslationsForTable } from "src/shared/traslations";
import { tableStyles } from "src/shared/util/tableStyles";
import { capitalizeWords } from "src/shared/util/textFunctions";
import './requirementsStyles.scss';

interface Props {
    open: boolean;
    onClose: () => void;
    originFieldActivityId: string;
    activeDiagnostic?: IUpdateDiagnosticForActivity;
    addRemoveOptimizationActivity: (activity: any, add: boolean) => void;
    onRequirementCreated: (requirementData: { types: string[], existingRequirements?: any[] }) => void;
}

const getModalStyle = (showCreateForm: boolean) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: showCreateForm ? 500 : 900,
    maxHeight: "90vh",
    bgcolor: "background.paper",
    boxShadow: 24,
    gap: 2,
    p: 4,
    overflow: "auto",
});

export const QuickAddRequirementModal = ({
    open,
    onClose,
    originFieldActivityId,
    activeDiagnostic,
    addRemoveOptimizationActivity,
    onRequirementCreated,
}: Props) => {
    const dispatch = useDispatch();
    const { locationId, projectId, locationName, lastStatusConditions, requirements: currentRequirements } = useSelector((state: any) => state.location);
    const { configModules } = useSelector(selectMainState);
    const { activeUser } = useSelector((state: any) => state.main);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [typesActivities, setTypesActivities] = useState<TypesOfActivities[]>([]);
    const [statusConditions, setStatusConditions] = useState<StatusConditions[]>([]);
    const [optimizationActivitiesStates, setOptimizationActivitiesStates] = useState<any[]>([]);
    const [listActiveBloquers, setlistActiveBloquers] = useState<any>([]);
    const [enableOptions, setEnableOptions] = useState<string[]>([]);
    const [reasonsOfPriority, setReasonsOfPriority] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [dataToTable, setDataToTable] = useState<any[]>([]);

    const [formValues, handleInputChange, reset, setNuewValues] = useForm({
        type: "",
        comment: "",
        status: "pending",
        priority: "normal",
        priorityReason: "",
        priorityExtraReason: "",
        requiredDate: moment().format("YYYY-MM-DD"),
        requiredUserId: activeUser?.id || "",
        locationId: locationId,
        projectId: projectId,
        typeActivity: "Requirement",
        statusActivity: "pending",
        originFieldActivityId: originFieldActivityId,
    });

    const { type, comment, status, priority, priorityReason, priorityExtraReason, requiredUserId } = formValues;

    // Configurar columnas de la tabla
    useEffect(() => {
        const optimizationActivitiesSelected = activeDiagnostic?.optimizationActivitiesSelected || [];

        setColumns([
            {
                field: "requiredDate",
                headerName: "Fecha Solicitado",
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "activity",
                headerName: "Tipo de Actividad",
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "requiredBy",
                headerName: "Solicitado por",
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "status",
                headerName: "Estado",
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "priority",
                headerName: "Prioridad",
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "actions",
                headerName: "",
                headerAlign: "center",
                align: "center",
                width: 120,
                renderCell: (params) => {
                    const id = `${params?.row?.optimizationActivityId}-${params?.row?.activity}`;
                    const optimizationActivitiesSelected = activeDiagnostic?.optimizationActivitiesSelected || [];
                    const selected = optimizationActivitiesSelected.find(
                        (item) => item.id === id
                    );

                    return (
                        <Grid
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 1,
                            }}
                        >
                            <IconButton
                                size="small"
                                color={selected ? "error" : "primary"}
                                onClick={() =>
                                    addRemoveOptimizationActivity(
                                        {...params.row, typeActivity: "Requirement"},
                                        !selected
                                    )
                                }
                            >
                                {selected ? (
                                    <DeleteForeverIcon fontSize="medium" />
                                ) : (
                                    <AddCircleOutlineIcon fontSize="medium" />
                                )}
                            </IconButton>
                        </Grid>
                    );
                },
            },
        ]);
    }, [activeDiagnostic, addRemoveOptimizationActivity]);

    // Preparar datos para la tabla
    useEffect(() => {
        setDataToTable(currentRequirements || []);
    }, [currentRequirements]);

    useEffect(() => {
        if (Array.isArray(configModules?.typesActivitiesForRequirements)) {
            setTypesActivities(configModules?.typesActivitiesForRequirements);
        }
        if (Array.isArray(configModules?.statusConditions)) {
            setStatusConditions(configModules?.statusConditions);
        }
        if (Array.isArray(configModules?.optimizationActivitiesStates)) {
            const filtedStates = configModules?.optimizationActivitiesStates?.filter(
                (state: any) => state?.type?.includes("Requirement")
            );
            setOptimizationActivitiesStates(filtedStates);
        }
        setReasonsOfPriority(configModules?.priorityReasonOptions || []);
    }, [configModules]);

    useEffect(() => {
        const blockers = [];
        const types = type.split(',').map((item: string) => item.trim()).filter(Boolean);

        if (types.length === 0) {
            setlistActiveBloquers([]);
            return;
        }

        for (const typeOfAction of types) {
            const typeActivity = typesActivities.find(
                (activity: any) => activity.label === typeOfAction
            );
            const values: any = { ...lastStatusConditions };

            for (const blocker of typeActivity?.deppendOf || []) {
                const statusBlocker = statusConditions.find(
                    (status: any) => status.field === blocker
                )?.warningStatus;

                if (statusBlocker?.includes(values[blocker])) {
                    blockers.push(blocker);
                }
            }
        }

        setlistActiveBloquers(blockers);
    }, [formValues, typesActivities, statusConditions, lastStatusConditions]);

    useEffect(() => {
        const enableOptionsFiltered = [];

        for (const activity of typesActivities) {
            const exist = currentRequirements.find(
                (requirement: any) => requirement.variableId === activity.id && requirement.status === "pending"
            );
            if (!exist) {
                enableOptionsFiltered.push(activity.label);
            }
        }
        setEnableOptions(enableOptionsFiltered);
    }, [typesActivities, currentRequirements]);

    const handleClose = () => {
        reset();
        setErrors([]);
        setShowCreateForm(false);
        onClose();
    };

    const rows = dataToTable.map((item: any) => {
        return {
            variableId: item.variableId,
            optimizationActivityId: item.optimizationActivityId,
            activity: item.activity,
            blocker: item.blocker,
            comments: item.comments,
            executedDate: moment(item.executedDate).format("DD-MM-YYYY"),
            originFieldActivityExecutedDate: moment(
                item.originFieldActivityExecutedDate
            ).format("DD-MM-YYYY"),
            originFieldActivityId: item.originFieldActivityId,
            requiredBy: item.requiredBy,
            requiredById: item.requiredById,
            requiredDate: moment(item.requiredDate).format("DD-MM-YYYY"),
            status: item.status,
            priority: item.priority || "normal",
        };
    });

    const handleSubmit = () => {
        const errors = [];
        const messageWithErrors = [];

        // Obtener el originFieldActivityId del prop o del formValues
        const finalOriginFieldActivityId = originFieldActivityId || formValues.originFieldActivityId;

        if (!type || type.trim() === "") {
            errors.push("type");
            messageWithErrors.push("El campo Tipo de Actividad es obligatorio");
        }

        if (!requiredUserId) {
            errors.push("requiredUserId");
            messageWithErrors.push("El campo requerido por es obligatorio");
        }

        // Validar que originFieldActivityId no esté vacío
        if (!finalOriginFieldActivityId || finalOriginFieldActivityId.trim() === "") {
            errors.push("originFieldActivityId");
            messageWithErrors.push("El campo Actividad asociada es obligatorio");
        }

        setErrors(errors);
        if (errors.length > 0) {
            Swal.fire({
                title: "Error",
                html: `<p>No se puede agregar debido a los siguientes errores:</p><ul>${messageWithErrors.map(error => `<li>${error}</li>`).join('')}</ul>`,
                icon: "error",
                confirmButtonText: "Aceptar",
            });
            return;
        }

        const blocker = listActiveBloquers?.map((blocker: any) => {
            const values: any = { ...lastStatusConditions };
            const statusBlocker = statusConditions.find(
                (status: any) => status.field === blocker
            );
            return { [String(statusBlocker?.name)]: values[blocker] };
        });

        const types = type.split(',').map((item: string) => item.trim()).filter(Boolean);

        // Verificar si alguna de las actividades ya existe en requirements
        // con un estado distinto a 'completed' o 'discarded'
        const existingRequirements: any[] = [];
        const newRequirements: string[] = [];

        for (const typeOfAction of types) {
            const element = typesActivities.find(
                (activity: any) => normalizeText(activity.label) === normalizeText(typeOfAction)
            );

            if (!element?.id) {
                continue;
            }

            // Buscar si existe un requerimiento con el mismo variableId y estado válido
            const existingRequirement = currentRequirements.find((req: any) => 
                req.variableId === element.id && 
                req.status !== 'completed' && 
                req.status !== 'discarded'
            );

            if (existingRequirement) {
                existingRequirements.push({
                    optimizationActivityId: existingRequirement.optimizationActivityId,
                    activity: typeOfAction,
                });
            } else {
                newRequirements.push(typeOfAction);
            }
        }

        // Si hay requerimientos existentes, prepararlos para agregar al diagnóstico
        if (existingRequirements.length > 0 && newRequirements.length === 0) {
            // Todos los requerimientos ya existen, solo agregarlos al diagnóstico
            onRequirementCreated({
                types: [],
                existingRequirements: existingRequirements,
            });

            Swal.fire({
                title: "Éxito",
                text: existingRequirements.length === 1 
                    ? "El requerimiento ya existe y ha sido agregado al diagnóstico"
                    : `${existingRequirements.length} requerimientos ya existen y han sido agregados al diagnóstico`,
                icon: "success",
                confirmButtonText: "Aceptar",
            });
            handleClose();
            return;
        }

        // Si hay requerimientos nuevos, crearlos
        if (newRequirements.length > 0) {
            const finalPriorityReason = priority === "normal" ? "" : priorityReason;
            const finalPriorityExtraReason = finalPriorityReason === "Otra" ? priorityExtraReason : "";

            const requirements = [];

            for (const typeOfAction of newRequirements) {
                const element = typesActivities.find(
                    (activity: any) => normalizeText(activity.label) === normalizeText(typeOfAction)
                );

                if (!element?.id) {
                    continue;
                }

                requirements.push({
                    id: element.id,
                    location: locationName,
                    name: typeOfAction,
                    value: "Si",
                    type: "Requirement",
                    comments: comment || "",
                    blocker: blocker || [],
                    status,
                    priority,
                    priorityReason: finalPriorityReason,
                    priorityExtraReason: finalPriorityExtraReason,
                } as any);
            }

            // Construir el objeto data con solo los campos requeridos en el nivel superior
            const data: any = {
                requiredDate: formValues.requiredDate,
                requiredUserId: formValues.requiredUserId,
                locationId: formValues.locationId,
                projectId: formValues.projectId,
                type: "Requirement",
                status: "pending",
                originFieldActivityId: finalOriginFieldActivityId,
                requirements,
            };

            dispatch(AddRequirementsInLocation(data));
            
            // Mensaje según si se agregaron existentes y nuevas
            let message = "";
            if (existingRequirements.length > 0) {
                message = `${existingRequirements.length} requerimiento(s) existente(s) agregado(s) y ${newRequirements.length} nuevo(s) creado(s)`;
            } else {
                message = "Requerimiento creado exitosamente";
            }

            Swal.fire({
                title: "Éxito",
                text: message,
                icon: "success",
                confirmButtonText: "Aceptar",
            });

            // Pasar los datos necesarios para que el componente padre busque y seleccione los requerimientos nuevos
            // También incluir los requerimientos existentes si los hay
            onRequirementCreated({
                types: newRequirements,
                existingRequirements: existingRequirements.length > 0 ? existingRequirements : undefined,
            });
        }

        setShowCreateForm(false);
        reset();
    };

    const paginationModel = { page: 0, pageSize: 10 };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={getModalStyle(showCreateForm)}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                backgroundColor: "#cf1919",
                                color: "#fff",
                                "&:hover": {
                                    backgroundColor: "#ff3535",
                                },
                                width: 30,
                                height: 30,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ fontSize: "1rem", ml: 3, fontWeight: "bold" }}>
                            {showCreateForm ? "Crear Nuevo Requerimiento" : "Requerimientos"}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {!showCreateForm && (
                            <IconButton
                                onClick={() => setShowCreateForm(true)}
                                sx={{
                                    backgroundColor: "#03365e",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "#129ED3",
                                    },
                                    width: 30,
                                    height: 30,
                                }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {!showCreateForm ? (
                    // Vista de lista
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <DataGrid
                                rows={getTranslationsForTable([...rows], "es", [
                                    "status",
                                ])}
                                columns={columns}
                                initialState={{ pagination: { paginationModel } }}
                                pageSizeOptions={[10, 20, 50, 100]}
                                checkboxSelection={false}
                                sx={{
                                    mt: 2,
                                    height: 400,
                                    ...tableStyles
                                }}
                                getRowId={(row) =>
                                    row.optimizationActivityId + row.variableId
                                }
                            />
                        </Grid>
                    </Grid>
                ) : (
                    // Vista de formulario
                    <>
                        <Grid sx={{ padding: 1 }}>
                            <InputLabel id="demo-simple-select-label">
                                Tipo de Actividad
                            </InputLabel>
                            <Autocomplete
                                multiple
                                id="size-small-standard-multi"
                                size="small"
                                options={enableOptions}
                                value={type !== "" ? type?.split(',').map((item: string) => item.trim()).filter(Boolean) : []}
                                onChange={(event: any, newValue: any) => {
                                    setNuewValues({
                                        ...formValues,
                                        type: newValue.join(',')
                                    });
                                }}
                                sx={{
                                    borderRadius: "4px",
                                    padding: "1px 1px",
                                    border: errors.includes("type") ? "1px solid red" : "none",
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        error={errors.includes("type")}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={{ padding: 1 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="demo-simple-select-label">
                                    Estado
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    label="Estado"
                                    id="demo-simple-select"
                                    name="status"
                                    value={status}
                                    onChange={handleInputChange}
                                    size="small"
                                >
                                    {optimizationActivitiesStates.map((state: any, index) => {
                                        return (
                                            <MenuItem value={state.id} key={index}>
                                                {capitalizeWords(state.name)}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid className="container_location_status" sx={{ paddingTop: 2 }}>
                            {listActiveBloquers?.map((blocker: any, index: number) => {
                                const values: any = { ...lastStatusConditions };
                                const statusBlocker = statusConditions.find(
                                    (status: any) => status.field === blocker
                                );
                                return (
                                    <Chip
                                        key={index}
                                        className="warningChip"
                                        label={`${statusBlocker?.name} : ${values[blocker]}`}
                                        variant="outlined"
                                    />
                                );
                            })}
                        </Grid>
                        <Grid className="priority_section">
                            <InputLabel sx={{ paddingLeft: 1 }}>Prioridad</InputLabel>
                            <ButtonGroup
                                variant="outlined"
                                fullWidth
                                aria-label="outlined button group"
                                className="internal_button_group_Section"
                            >
                                {["Normal", "Alta", "Urgente"].map((button, index) => (
                                    <Button
                                        key={index}
                                        variant={formValues.priority === button.toLowerCase() ? "contained" : "outlined"}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setNuewValues({
                                                ...formValues,
                                                priority: button.toLowerCase()
                                            });
                                        }}
                                        size="small"
                                    >
                                        {button}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Grid>
                        {priority !== "normal" && (
                            <>
                                <Grid sx={{ mt: 2, pr: 1, pl: 1 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="demo-simple-select-label">
                                            Razón de Priorizar el Requerimiento
                                        </InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            label="Razón de Priorizar el Requerimiento"
                                            id="demo-simple-select"
                                            name="priorityReason"
                                            value={priorityReason}
                                            onChange={handleInputChange}
                                            size="small"
                                        >
                                            {reasonsOfPriority.map((reason: any, index) => {
                                                return (
                                                    <MenuItem value={reason} key={index}>
                                                        {capitalizeWords(reason)}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {priorityReason === "Otra" && (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="razón de priorización"
                                        sx={{ mt: 2, pr: 1, pl: 1 }}
                                        value={priorityExtraReason}
                                        name="priorityExtraReason"
                                        onChange={handleInputChange}
                                    />
                                )}
                            </>
                        )}
                        <TextField
                            fullWidth
                            size="small"
                            label="Comentario"
                            multiline
                            rows={4}
                            sx={{ mt: 2 }}
                            value={comment}
                            name="comment"
                            onChange={handleInputChange}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="small"
                            sx={{ mt: 2 }}
                            onClick={handleSubmit}
                        >
                            AGREGAR
                        </Button>
                    </>
                )}
            </Box>
        </Modal>
    );
};

